import { useEffect, useState } from 'react';
import { Aviso, Vazio } from '../ui';
import { CartaoDePendencia } from './CartaoDePendencia';
import { comNumero, ordenarPendencias, type GrupoDoResumo, type Pendencia } from './tipos';

/**
 * A pilha de cartões e os filtros dela (RN-256, 3 e 4).
 *
 * Três filtros, que se somam:
 *
 *  · o do resumo (quem escolhe é o `ResumoDoPainel`; aqui só se aplica);
 *  · «Tudo | Comigo» (M1), em cima da pilha: em «Comigo» ficam as situações cuja permissão a
 *    pessoa tem — quem resolve é ela. Uma situação sem permissão conhecida
 *    fica nos dois: na dúvida, não se esconde de ninguém um problema que
 *    pode ser seu. A permissão `'a|b'` quer dizer «qualquer uma das duas
 *    basta», e quem entende o `|` é a pilha: quem monta o painel passa o
 *    `pode` da sessão, como ele é;
 *  · a área (os chips, «Tudo» primeiro), com a contagem de cada uma depois
 *    dos dois filtros acima — o número do chip é o que aparece ao tocá-lo.
 *
 * A ordem é sempre a mesma (`ordenarPendencias`): Trava → Atenção → Para
 * saber, e o mais parado primeiro. Cartão com zero não aparece.
 *
 * Sem nenhuma situação de trava ou de atenção, a frase do dia tranquilo
 * vem no alto (com o complemento que o painel da área quiser), e o que é só
 * para saber logo abaixo. A frase é o `Aviso` de sucesso da casa (P-UI-16:
 * o título na cor, o complemento na tinta), e o filtro que zera a pilha
 * mostra o `Vazio` da casa (P-UI-30) — nenhum dos dois com desenho próprio.
 *
 * A área escolhida que deixa de existir (o painel se atualizou e ela não
 * tem mais cartão) não fica presa: a pilha volta a «Tudo», e o chip «Tudo»
 * volta a ficar marcado.
 */

/** As frases da tela (em constante: o 305.10 lê o código sem as strings). */
const DIA_TRANQUILO = 'Nada travado e nada esperando alguém.';
const NADA_COM_O_FILTRO = 'Nenhuma situação com estes filtros.';
const TUDO = 'Tudo';
const COMIGO = 'Comigo';

export function PilhaDePendencias({
  pendencias, filtroDoResumo, comigo, aoComigo, pode, novos, complementoDoTranquilo,
}: {
  pendencias: Pendencia[];
  filtroDoResumo: GrupoDoResumo | null;
  comigo: boolean;
  aoComigo(v: boolean): void;
  /** O `pode` da sessão: uma permissão por vez (o `|` a pilha resolve). */
  pode(perm: string): boolean;
  /** Quanto cada situação subiu desde a última visita (M2), pela chave. */
  novos: Record<string, number>;
  /** Uma frase depois do «Nada travado e nada esperando alguém.». */
  complementoDoTranquilo?: string;
}) {
  const [area, setArea] = useState<string | null>(null);

  const existentes = comNumero(pendencias);
  const tranquilo = !existentes.some((p) => p.gravidade === 'trava' || p.gravidade === 'atencao');

  const doResumo = filtroDoResumo ? existentes.filter((p) => p.grupo === filtroDoResumo) : existentes;
  /* `medicao.conferir|medicao.aprovar`: qualquer uma das duas basta. */
  const podeAlguma = (perm: string) => perm.split('|').some((x) => pode(x.trim()));
  const minhas = doResumo.filter((p) => !p.permissao || podeAlguma(p.permissao));
  const daPessoa = comigo ? minhas : doResumo;

  /* As áreas na ordem em que o adaptador as entrega; a escolhida continua
     no chip mesmo se o filtro de cima a zerou, para dar para desmarcar. */
  const areas = [...new Set(existentes.map((p) => p.area))];
  const escolhida = area !== null && areas.includes(area) ? area : null;
  const sumiu = area !== null && escolhida === null;
  useEffect(() => { if (sumiu) setArea(null); }, [sumiu]);
  const contagem = (a: string) => daPessoa.filter((p) => p.area === a).length;
  const naArea = escolhida ? daPessoa.filter((p) => p.area === escolhida) : daPessoa;
  const pilha = ordenarPendencias(naArea);

  return (
    <div className="pn-pilha">
      {tranquilo && (
        <Aviso tipo="ok" titulo={DIA_TRANQUILO}>
          {complementoDoTranquilo}
        </Aviso>
      )}

      {existentes.length > 0 && (
        <div className="pn-filtros">
          <div className="pn-de-quem" role="group" aria-label="De quem">
            <button type="button" className="pn-quem" aria-pressed={!comigo} onClick={() => aoComigo(false)}>
              {TUDO} <b>{doResumo.length}</b>
            </button>
            <button type="button" className="pn-quem" aria-pressed={comigo} onClick={() => aoComigo(true)}>
              {COMIGO} <b>{minhas.length}</b>
            </button>
          </div>
          <div className="pn-areas" role="group" aria-label="Áreas">
            <button type="button" className="pn-area" aria-pressed={!escolhida} onClick={() => setArea(null)}>
              {TUDO} <b>{daPessoa.length}</b>
            </button>
            {areas.map((a) => (
              <button key={a} type="button" className="pn-area" aria-pressed={escolhida === a}
                onClick={() => setArea(escolhida === a ? null : a)}>
                {a} <b>{contagem(a)}</b>
              </button>
            ))}
          </div>
        </div>
      )}

      {pilha.length > 0 ? (
        <div className="pn-cartoes">
          {pilha.map((p) => <CartaoDePendencia key={p.chave} p={p} novo={novos[p.chave]} />)}
        </div>
      ) : existentes.length > 0 && (
        <Vazio>{NADA_COM_O_FILTRO}</Vazio>
      )}
    </div>
  );
}
